import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Text } from '@contrast-security-inc/design-system-typography'
import { RenderDialogProps } from '../../legacy-design-system/action/confirmation-action'
import { Dialog, DialogFooter } from '../../legacy-design-system/modal'
import { PureQueryOptions } from 'apollo-client'
import { css, cx } from '@emotion/css'
import { logError, logSuccess } from '../toaster'
import { useApplicationModules } from './api/use-application-modules'
import { ApplicationModulesGrid } from './application-modules-grid'
import { useContrastMutation } from '../../hooks/use-contrast-mutation'
import { unmergeModules, UnmergeModulesArgs } from './api/unmerge-modules'
import { BaseApiResponse, HTTPError } from '../../api/fetch/decorate-fetch'
import {
  ApplicationModule,
  invalidateApplicationModuleQueries
} from './api/get-application-modules'
import { useQueryClient } from 'react-query'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { hasRole } from 'contrastBase/RoleSwitch'
import { Role } from '../roles/role-switch'
import { Button } from '@contrast-security-inc/design-system-buttons'
import {
  unmergeAllModules,
  UnmergeAllModulesArgs
} from './api/unmerge-all-modules'

const mediumModalMinScreenWidth = 917
const triggerLoaderModuleCount = 500

interface Application {
  readonly id: string
  readonly name: string
  readonly licensed: boolean
  readonly role: Role
}

interface UnmergeDialogProps extends RenderDialogProps {
  organizationID: string
  organizationRole: Role
  primaryApplication: Application
  moduleCount: number
  refetchQueries?: PureQueryOptions[]
  refresh?: () => Promise<any>
}

export interface SelectModuleProps {
  readonly moduleID: string
  readonly allModules: string[]
  readonly moduleSelections: ModuleSelections
  readonly allSelected: boolean
  readonly setModuleSelections: React.Dispatch<
    React.SetStateAction<ModuleSelections>
  >
  readonly setAllSelected: React.Dispatch<React.SetStateAction<boolean>>
}

export interface SelectPrimaryProps {
  readonly allModules: string[]
  readonly allSelected: boolean
  readonly setModuleSelections: React.Dispatch<
    React.SetStateAction<ModuleSelections>
  >
  readonly setAllSelected: React.Dispatch<React.SetStateAction<boolean>>
}

const dialogMessageCss = css`
  margin-bottom: 16px;
`

const buttonCss = css`
  margin-left: 8px;
  height: 34px;
  font-size: 14px;
`

const unmergeDialogTestId = 'unmerge-dialog-test-id'
export const headerModuleCountTestId = 'unmerge-header-module-count-test-id'
export const unmergeModulesButtonTestId = 'unmerge-modules-button-test-id'
export const unmergeModulesLoadingText = 'unmerge-modules-loading-text'

function CommonHeader({ primaryApplication, moduleCount, className = '' }) {
  const intl = useIntl()

  return (
    <div className={cx(dialogMessageCss, className)}>
      <Text font="body">
        {intl.formatMessage(
          {
            id: primaryApplication.licensed
              ? 'variable_primaryLicensedApplicationComponent'
              : 'variable_primaryUnlicensedApplicationComponent',
            defaultMessage: primaryApplication.licensed
              ? '{primaryAppName} is a LICENSED application that represents {moduleCount} modules.'
              : '{primaryAppName} is an UNLICENSED application that represents {moduleCount} modules.'
          },
          {
            primaryAppName: (
              <Text font="bodyStrong">{primaryApplication.name}</Text>
            ),
            moduleCount
          }
        )}
      </Text>
    </div>
  )
}

function LoadingMessage() {
  const intl = useIntl()

  return (
    <div
      className={css`
        background-color: ${theme.color.neutral10};
        color: ${theme.color.orange50};
        padding: 16px 12px 16px 20px;
      `}
    >
      <Text color="neutral60" font="body" testId={unmergeModulesLoadingText}>
        {intl.formatMessage({ id: 'title_loadingAppModules' })}
      </Text>
    </div>
  )
}

export interface ModalBodyProps {
  moduleCount: number
  allSelected: boolean
  moduleSelections: ModuleSelections
  setAllSelected: React.Dispatch<React.SetStateAction<boolean>>
  setModuleSelections: React.Dispatch<React.SetStateAction<ModuleSelections>>
}

function sortApplicationModulesByGradeAscending(
  moduleA: ApplicationModule,
  moduleB: ApplicationModule
) {
  return moduleA.grade - moduleB.grade
}

function Body({
  organizationID,
  primaryApplication,
  moduleCount,
  allSelected,
  moduleSelections,
  setAllSelected,
  setModuleSelections
}: {
  organizationID: string
  primaryApplication: Application
} & ModalBodyProps) {
  const { data, isLoading } = useApplicationModules(
    organizationID,
    primaryApplication.id
  )

  data.sort(sortApplicationModulesByGradeAscending)

  return (
    <div>
      <CommonHeader
        primaryApplication={primaryApplication}
        moduleCount={moduleCount}
        className={css`
          margin-top: 16px;
          padding: 20px;
        `}
      />
      {isLoading && moduleCount > triggerLoaderModuleCount && (
        <LoadingMessage />
      )}
      <ApplicationModulesGrid
        isLoading={isLoading}
        applicationModules={data}
        moduleCount={moduleCount}
        allSelected={allSelected}
        moduleSelections={moduleSelections}
        setAllSelected={setAllSelected}
        setModuleSelections={setModuleSelections}
        onPrimarySelection={onPrimarySelection}
        onModuleSelection={onModuleSelection}
      />
    </div>
  )
}

function getWindowWidth() {
  const { innerWidth: width } = window

  return {
    width
  }
}

function useWindowWidth() {
  const [windowWidth, setWindowWidth] = useState(getWindowWidth())

  useEffect(() => {
    function handleResize() {
      setWindowWidth(getWindowWidth())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowWidth
}

interface ModuleSelections {
  readonly [id: string]: boolean
}

export function UnmergeModulesDialog({
  organizationID,
  primaryApplication,
  moduleCount,
  unmerge,
  isOpen,
  closeDialog,
  unmergeAllApps,
  organizationRole: _organizationRole
}: UnmergeDialogProps & {
  readonly unmerge: (moduleIDs: string[], close: () => void) => void
  readonly unmergeAllApps: (close: () => void) => void
}) {
  const intl = useIntl()
  const [moduleSelections, setModuleSelections] =
    React.useState<ModuleSelections>({})

  const [allSelected, setAllSelected] = React.useState(false)

  const selections = getSelections(moduleSelections)

  function close() {
    closeDialog()
    setAllSelected(false)
    setModuleSelections({})
  }

  const { width } = useWindowWidth()

  return (
    <Dialog
      testId={unmergeDialogTestId}
      size={width >= mediumModalMinScreenWidth ? 'medium' : 'large'} // resize modal based on screen width
      isOpen={isOpen}
      onCancel={close}
      renderTitle={() => (
        <>
          {intl.formatMessage({
            id: 'title_applicationModules',
            defaultMessage: 'Application Modules'
          })}
          <span data-testid={headerModuleCountTestId}> ({moduleCount}) </span>
        </>
      )}
    >
      <Body
        organizationID={organizationID}
        primaryApplication={primaryApplication}
        moduleCount={moduleCount}
        allSelected={allSelected}
        moduleSelections={moduleSelections}
        setAllSelected={setAllSelected}
        setModuleSelections={setModuleSelections}
      />
      <DialogFooter>
        <Button
          variant="secondary"
          className={buttonCss}
          onClick={() => {
            close()
          }}
        >
          {intl.formatMessage({ id: 'action_cancel' })}
        </Button>
        <Button
          variant="primary"
          data-e2e="unmerge-button"
          data-testid={unmergeModulesButtonTestId}
          autoFocus
          className={buttonCss}
          onClick={() => {
            allSelected ? unmergeAllApps(close) : unmerge(selections, close)
          }}
          isDisabled={
            !hasRole(primaryApplication.role, 'EDIT') ||
            (!allSelected && selections.length <= 0)
          }
        >
          {intl.formatMessage({
            id: 'action_applicationModules_unmergeSelected_application'
          })}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

function onModuleSelection({
  moduleID,
  allModules,
  moduleSelections: prevModuleSelections,
  setAllSelected,
  setModuleSelections
}: SelectModuleProps) {
  const moduleSelections = {
    ...prevModuleSelections,
    [moduleID]: !prevModuleSelections[moduleID]
  }

  setModuleSelections(moduleSelections)
  setAllSelected(getSelections(moduleSelections).length === allModules.length)
}

function onPrimarySelection({
  allModules,
  allSelected,
  setAllSelected,
  setModuleSelections
}: SelectPrimaryProps) {
  const moduleSelections = allSelected
    ? {}
    : allModules.reduce((acc, curr) => {
        return { ...acc, [curr]: true }
      }, {})

  setModuleSelections(moduleSelections)
  setAllSelected(!allSelected)
}

function getSelections(moduleSelections: ModuleSelections): string[] {
  return Object.keys(moduleSelections).reduce((acc, curr) => {
    // eslint-disable-next-line unicorn/prefer-ternary
    if (moduleSelections[curr]) {
      return [...acc, curr]
    } else {
      return acc
    }
  }, [])
}

export function UnmergeDialog({
  organizationID,
  refresh,
  primaryApplication,
  ...props
}: UnmergeDialogProps) {
  const intl = useIntl()

  const queryClient = useQueryClient()

  const { mutate: unmergeModulesMutate } = useContrastMutation<
    BaseApiResponse[],
    HTTPError,
    UnmergeModulesArgs
  >(unmergeModules)

  const { mutate: unmergeAllModulesMutate } = useContrastMutation<
    BaseApiResponse,
    HTTPError,
    UnmergeAllModulesArgs
  >(unmergeAllModules)

  const handleUnmergeModules = (moduleIds: string[], close: () => void) => {
    return unmergeModulesMutate(
      { organizationID, moduleIds },
      {
        onSuccess: async () => {
          if (refresh) {
            await refresh()
          }
          await invalidateApplicationModuleQueries(queryClient)
          close()
          logSuccess(
            intl.formatMessage({
              id: 'detail_unmergeApplications_successfullyUnmergedApps'
            })
          )
        },
        onError: () => {
          logError(
            intl.formatMessage({
              id: 'detail_unmergeApplications_errorUnmergingApps'
            })
          )
        }
      }
    )
  }

  const handleUnmergeAllModules = (close: () => void) => {
    return unmergeAllModulesMutate(
      {
        organizationID,
        primaryApplicationID: primaryApplication.id
      },
      {
        onSuccess: async () => {
          if (refresh) {
            await refresh()
          }
          await invalidateApplicationModuleQueries(queryClient)
          close()
          logSuccess(
            intl.formatMessage({
              id: 'detail_unmergeApplications_successfullyUnmergedApps'
            })
          )
        },
        onError: () => {
          logError(
            intl.formatMessage({
              id: 'detail_unmergeApplications_errorUnmergingApps'
            })
          )
        }
      }
    )
  }

  return (
    <UnmergeModulesDialog
      {...props}
      primaryApplication={primaryApplication}
      organizationID={organizationID}
      unmerge={handleUnmergeModules}
      unmergeAllApps={handleUnmergeAllModules}
    />
  )
}
